import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {
  usePlatformBanner,
  BANNER_THEMES,
  BANNER_DEFAULTS,
  saveBannerConfig,
  loadBannerConfig,
} from '../../hooks/usePlatformBanner';

const {
  FiAlertTriangle, FiSave, FiRefreshCw, FiCheck, FiToggleLeft,
  FiToggleRight, FiEye, FiEyeOff, FiClock, FiX,
  FiInfo, FiCheckCircle, FiAlertCircle, FiTool, FiBell,
  FiCalendar, FiDroplet,
} = FiIcons;

// ── Helpers ───────────────────────────────────────────────────────────────────
const Toggle = ({ active, onToggle, warn = false }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`w-12 h-6 rounded-full relative flex-shrink-0 transition-colors duration-300 ${
      active ? (warn ? 'bg-red-500' : 'bg-accent-gold') : 'bg-brand-dark'
    }`}
  >
    <motion.div
      animate={{ x: active ? 24 : 2 }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm"
    />
  </button>
);

const ToggleRow = ({ label, desc, value, onChange, warn = false, tag }) => (
  <div className={`flex items-center justify-between gap-4 p-5 rounded-2xl border transition-colors ${
    warn && value
      ? 'bg-red-50 border-red-200'
      : 'bg-brand-light border-brand-dark hover:border-accent-gold/50'
  }`}>
    <div className="flex-grow min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <p className={`font-bold text-sm ${warn && value ? 'text-red-700' : 'text-accent-navy'}`}>{label}</p>
        {tag && (
          <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-accent-navy text-white">
            {tag}
          </span>
        )}
      </div>
      <p className={`text-[11px] leading-relaxed mt-0.5 ${warn && value ? 'text-red-500' : 'text-text-muted'}`}>{desc}</p>
    </div>
    <Toggle active={value} onToggle={() => onChange(!value)} warn={warn && value} />
  </div>
);

// ── Live Banner Preview ───────────────────────────────────────────────────────
const BannerPreview = ({ config }) => {
  const themeMeta = BANNER_THEMES.find(t => t.id === config.bannerTheme) || BANNER_THEMES[0];
  const bgStyle   = config.bannerTheme === 'custom' && config.bannerColor
    ? { backgroundColor: config.bannerColor }
    : {};
  const bgClass   = config.bannerTheme !== 'custom' ? themeMeta.bg : '';

  const ICONS = {
    warning: FiAlertTriangle, info: FiInfo,
    danger: FiAlertCircle, success: FiCheckCircle,
    maintenance: FiTool, custom: FiAlertTriangle,
  };
  const Icon = ICONS[config.bannerTheme] || FiAlertTriangle;

  return (
    <div className={`w-full rounded-2xl overflow-hidden border-2 border-dashed border-brand-dark/30 ${!config.platform_maintenance ? 'opacity-50' : ''}`}>
      <div className="bg-brand-light px-4 py-2 border-b border-brand-dark/20">
        <p className="text-[9px] font-black uppercase tracking-widest text-text-muted flex items-center gap-1.5">
          <SafeIcon icon={FiEye} className="w-3 h-3" />
          Live Preview — Visitor View
        </p>
      </div>
      <div className={`${bgClass} py-2.5 px-4 flex items-center justify-between gap-4`} style={bgStyle}>
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          {config.showIcon && <SafeIcon icon={Icon} className="w-4 h-4 text-white flex-shrink-0" />}
          <p className="text-white text-xs font-semibold truncate">
            {config.bannerText || 'Your banner message will appear here…'}
          </p>
        </div>
        {config.dismissible && (
          <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <SafeIcon icon={FiX} className="w-3 h-3 text-white" />
          </div>
        )}
      </div>
    </div>
  );
};

// ── Save Toast ────────────────────────────────────────────────────────────────
const SaveToast = ({ show, message = 'Banner settings saved' }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.9 }}
        className="fixed bottom-8 right-8 z-[300] bg-green-500 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3"
      >
        <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
          <SafeIcon icon={FiCheck} className="w-4 h-4" />
        </div>
        <div>
          <p className="text-xs font-black uppercase tracking-widest">Saved</p>
          <p className="text-[10px] opacity-80">{message}</p>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
const PlatformBannerSettings = () => {
  const { config: liveConfig, update } = usePlatformBanner();

  // Local draft — only pushed to storage on Save
  const [draft, setDraft] = useState(() => loadBannerConfig());
  const [showToast, setShowToast]       = useState(false);
  const [toastMsg,  setToastMsg]        = useState('');

  const patch = useCallback((key, value) => {
    setDraft(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleSave = () => {
    saveBannerConfig(draft);
    setToastMsg('Banner configuration updated platform-wide.');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleActivateNow = () => {
    const next = { ...draft, platform_maintenance: true };
    setDraft(next);
    saveBannerConfig(next);
    setToastMsg('Banner is now LIVE for all visitors.');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleDeactivate = () => {
    const next = { ...draft, platform_maintenance: false };
    setDraft(next);
    saveBannerConfig(next);
    setToastMsg('Banner deactivated.');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleReset = () => {
    setDraft({ ...BANNER_DEFAULTS });
    saveBannerConfig({ ...BANNER_DEFAULTS });
    setToastMsg('Reset to defaults.');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const isLive = liveConfig.platform_maintenance;

  return (
    <div className="space-y-8">

      {/* ── Status header ── */}
      <div className={`rounded-[32px] p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-2 transition-all ${
        isLive
          ? 'bg-red-50 border-red-300'
          : 'bg-green-50 border-green-300'
      }`}>
        <div className="flex items-center gap-5">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0 ${
            isLive ? 'bg-red-500' : 'bg-green-500'
          }`}>
            <SafeIcon icon={isLive ? FiAlertTriangle : FiCheckCircle} className="w-8 h-8 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h3 className={`font-serif text-2xl ${isLive ? 'text-red-800' : 'text-green-800'}`}>
                {isLive ? 'Banner Is LIVE' : 'Banner Is Inactive'}
              </h3>
              <span className={`text-[9px] px-2.5 py-1 rounded-full font-black uppercase tracking-widest animate-pulse ${
                isLive ? 'bg-red-200 text-red-700' : 'bg-green-200 text-green-700'
              }`}>
                {isLive ? 'Active' : 'Off'}
              </span>
            </div>
            <p className={`text-sm font-light ${isLive ? 'text-red-600' : 'text-green-600'}`}>
              {isLive
                ? 'All visitors currently see the maintenance banner across every page.'
                : 'No banner is displayed to visitors. Configure below and activate when ready.'}
            </p>
          </div>
        </div>
        <div className="flex gap-3 flex-shrink-0">
          {isLive ? (
            <button
              onClick={handleDeactivate}
              className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-red-300 text-red-600 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-red-50 transition-all shadow-sm"
            >
              <SafeIcon icon={FiEyeOff} className="w-4 h-4" />
              Deactivate Banner
            </button>
          ) : (
            <button
              onClick={handleActivateNow}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-red-700 transition-all shadow-lg shadow-red-200"
            >
              <SafeIcon icon={FiBell} className="w-4 h-4" />
              Activate Banner Now
            </button>
          )}
        </div>
      </div>

      {/* ── Live preview ── */}
      <BannerPreview config={draft} />

      {/* ── Master toggle + text ── */}
      <div className="bg-white rounded-[32px] border border-brand-dark p-8 space-y-6">
        <div className="flex items-center gap-4 pb-5 border-b border-brand-dark">
          <div className="p-3 bg-brand-light rounded-2xl">
            <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-accent-navy" />
          </div>
          <div>
            <h3 className="font-serif text-lg text-accent-navy">Banner Configuration</h3>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Master switch & message</p>
          </div>
        </div>

        <ToggleRow
          label="Enable Maintenance Banner"
          desc="When active, a site-wide banner is rendered at the very top of every page for all visitors."
          value={draft.platform_maintenance}
          onChange={v => patch('platform_maintenance', v)}
          warn={true}
          tag="Global"
        />

        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">
            Banner Message *
          </label>
          <textarea
            rows={3}
            value={draft.bannerText}
            onChange={e => patch('bannerText', e.target.value)}
            placeholder="e.g. We're performing scheduled maintenance. Some features may be temporarily unavailable."
            className="w-full px-5 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold transition-all text-sm font-light resize-none"
          />
          <p className="text-[10px] text-text-muted mt-1.5 text-right">
            {draft.bannerText?.length || 0} / 200 characters
          </p>
        </div>
      </div>

      {/* ── Theme & Appearance ── */}
      <div className="bg-white rounded-[32px] border border-brand-dark p-8 space-y-6">
        <div className="flex items-center gap-4 pb-5 border-b border-brand-dark">
          <div className="p-3 bg-brand-light rounded-2xl">
            <SafeIcon icon={FiDroplet} className="w-5 h-5 text-accent-navy" />
          </div>
          <div>
            <h3 className="font-serif text-lg text-accent-navy">Theme & Appearance</h3>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Color, style, and icon</p>
          </div>
        </div>

        {/* Theme picker */}
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-3">Banner Theme</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {BANNER_THEMES.map(theme => (
              <button
                key={theme.id}
                type="button"
                onClick={() => patch('bannerTheme', theme.id)}
                className={`flex items-center gap-3 p-4 rounded-2xl border-2 transition-all text-left ${
                  draft.bannerTheme === theme.id
                    ? 'border-accent-gold bg-accent-gold/5'
                    : 'border-brand-dark hover:border-accent-gold/40 bg-brand-light'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-xl flex items-center justify-center text-sm flex-shrink-0 ${theme.bg}`}
                  style={theme.id === 'custom' && draft.bannerColor ? { backgroundColor: draft.bannerColor } : {}}
                >
                  {theme.icon}
                </div>
                <div>
                  <p className="text-xs font-bold text-accent-navy">{theme.label}</p>
                  {draft.bannerTheme === theme.id && (
                    <p className="text-[9px] text-accent-gold font-black uppercase tracking-widest">Selected</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Custom color picker */}
        {draft.bannerTheme === 'custom' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="overflow-hidden"
          >
            <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">
              Custom Background Color
            </label>
            <div className="flex items-center gap-4">
              <input
                type="color"
                value={draft.bannerColor || '#1a2744'}
                onChange={e => patch('bannerColor', e.target.value)}
                className="w-14 h-14 rounded-2xl border-2 border-brand-dark cursor-pointer p-1"
              />
              <input
                type="text"
                value={draft.bannerColor || '#1a2744'}
                onChange={e => patch('bannerColor', e.target.value)}
                placeholder="#1a2744"
                className="flex-1 px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-mono"
              />
            </div>
          </motion.div>
        )}

        {/* Appearance toggles */}
        <div className="space-y-3 pt-2">
          <ToggleRow
            label="Show Icon Prefix"
            desc="Display a contextual icon at the start of the banner message."
            value={draft.showIcon}
            onChange={v => patch('showIcon', v)}
          />
          <ToggleRow
            label="Allow Visitors to Dismiss"
            desc="Show a close button so visitors can hide the banner for their current session."
            value={draft.dismissible}
            onChange={v => patch('dismissible', v)}
          />
        </div>
      </div>

      {/* ── Schedule (optional) ── */}
      <div className="bg-white rounded-[32px] border border-brand-dark p-8 space-y-6">
        <div className="flex items-center gap-4 pb-5 border-b border-brand-dark">
          <div className="p-3 bg-brand-light rounded-2xl">
            <SafeIcon icon={FiCalendar} className="w-5 h-5 text-accent-navy" />
          </div>
          <div>
            <h3 className="font-serif text-lg text-accent-navy">Scheduled Window</h3>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Optional auto start / end</p>
          </div>
        </div>

        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-2xl p-4">
          <SafeIcon icon={FiInfo} className="text-blue-500 w-4 h-4 mt-0.5 flex-shrink-0" />
          <p className="text-[10px] text-blue-700 leading-relaxed">
            Leave both fields empty to use the master toggle above as the only control.
            When a schedule window is set the banner will only display during that window,
            even if the master toggle is on.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">
              Start Date &amp; Time
            </label>
            <div className="relative">
              <SafeIcon icon={FiClock} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
              <input
                type="datetime-local"
                value={draft.startAt ? draft.startAt.slice(0, 16) : ''}
                onChange={e => patch('startAt', e.target.value ? new Date(e.target.value).toISOString() : null)}
                className="w-full pl-11 pr-5 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm"
              />
            </div>
            {draft.startAt && (
              <button onClick={() => patch('startAt', null)}
                className="text-[10px] text-red-400 hover:text-red-600 mt-1.5 font-bold transition-colors">
                Clear start time
              </button>
            )}
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-2">
              End Date &amp; Time
            </label>
            <div className="relative">
              <SafeIcon icon={FiClock} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
              <input
                type="datetime-local"
                value={draft.endAt ? draft.endAt.slice(0, 16) : ''}
                onChange={e => patch('endAt', e.target.value ? new Date(e.target.value).toISOString() : null)}
                className="w-full pl-11 pr-5 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm"
              />
            </div>
            {draft.endAt && (
              <button onClick={() => patch('endAt', null)}
                className="text-[10px] text-red-400 hover:text-red-600 mt-1.5 font-bold transition-colors">
                Clear end time
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Footer actions ── */}
      <div className="flex items-center justify-between pt-2 flex-wrap gap-4">
        <button
          type="button"
          onClick={handleReset}
          className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-brand-dark text-text-muted rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-red-300 hover:text-red-500 transition-all"
        >
          <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" />
          Reset to Defaults
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="flex items-center gap-3 px-10 py-4 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-xl active:scale-[0.98]"
        >
          <SafeIcon icon={FiSave} className="w-4 h-4" />
          Save Banner Settings
        </button>
      </div>

      <SaveToast show={showToast} message={toastMsg} />
    </div>
  );
};

export default PlatformBannerSettings;